export interface ReceiptItem {
  name: string;
  count: number;
  price: number;
  saleActive: boolean;
  salePrice: number;
}

export interface ReceiptData {
  businessName: string;
  employeeName: string;
  taxId: string;
  orderToken: string;
  paymentMethod: string;
  companyRegister?: string;
  phone?: string;
  items: ReceiptItem[];
}

const getPaymentMethodText = (method: string): string => {
  const paymentMethods: { [key: string]: string } = {
    'card': 'Карт',
    'qpay': 'Qpay',
    'account': 'Данс',
    'cash': 'Бэлэн'
  };
  return paymentMethods[method] || method;
};

export const generateReceiptHTML = (data: ReceiptData): string => {
  const now = new Date();
  const dateStr = now.toLocaleDateString('mn-MN');
  const timeStr = now.toLocaleTimeString('mn-MN');
  
  let itemsHTML = '';
  let totalAmount = 0;
  let totalCount = 0;
  
  data.items.forEach((item) => {
    const price = item.saleActive ? item.salePrice : item.price;
    const itemTotal = price * item.count;
    totalAmount += itemTotal;
    totalCount += item.count;
    
    itemsHTML += `
      <div class="item-row">
        <div class="h-name">${item.name}</div>
        <div class="h-price">${price.toLocaleString()}</div>
        <div class="h-qty">${item.count}</div>
        <div class="h-total">${itemTotal.toLocaleString()}</div>
      </div>
    `;
  });

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <title>Баримт</title>
      <style>
        @page {
          size: 58mm auto;
          margin: 0;
        }
        body {
          font-family: 'Courier New', monospace;
          font-size: 10px;
          line-height: 1.2;
          margin: 0;
          padding: 0;
          width: 58mm;
          color: #000;
          background: #fff;
        }
        .header {
          text-align: center;
          border-bottom: 1px dashed #000;
          padding-bottom: 5px;
          margin-bottom: 5px;
        }
        .company-name {
          font-weight: bold;
          font-size: 12px;
          margin-bottom: 2px;
        }
        .receipt-info {
          margin-bottom: 5px;
        }
        .inline {
          display: flex;
          justify-content: space-between;
          gap: 6px;
        }
        .item-row {
          display: flex;
          margin-bottom: 3px;
          padding-bottom: 2px;
        }
        .item-row .h-name { 
          flex: 0 0 40%; 
          text-align: left; 
          word-wrap: break-word;
          overflow-wrap: break-word;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
          line-height: 1.1;
        }
        .item-row .h-price { 
          flex: 0 0 20%; 
          text-align: right; 
        }
        .item-row .h-qty { 
          flex: 0 0 10%; 
          text-align: right; 
        }
        .item-row .h-total { 
          flex: 0 0 30%; 
          text-align: right; 
        }
        .total-section {
          margin-top: 5px;
          padding-top: 5px;
          text-align: left;
        }
        .total-amount {
          font-weight: bold;
          margin: 5px 0;
        }
        .payment-info {
          margin: 5px 0 3px 0;
        }
        .footer {
          text-align: left;
          margin-top: 10px;
          border-top: 1px dashed #000;
          padding-top: 5px;
        }
        .divider {
          border-top: 1px dashed #000;
          margin: 5px 0;
        }
        .items-header {
          display: flex;
          margin: 4px 0;
          padding-bottom: 2px;
          border-bottom: 1px dotted #000;
        }
        .items-header .h-name { flex: 0 0 40%; text-align: left; }
        .items-header .h-price { flex: 0 0 20%; text-align: right; }
        .items-header .h-qty { flex: 0 0 10%; text-align: right; }
        .items-header .h-total { flex: 0 0 30%; text-align: right; }
        .items-summary {
          display: flex;
          justify-content: space-between;
          font-weight: bold;
          margin-top: 6px;
          padding-top: 4px;
          border-top: 1px dashed #000;
        }
      </style>
    </head>
    <body>
      <div class="header">
        <div class="company-name">${data.businessName}</div>
        <div class="divider"></div>
        <div class="receipt-info inline"><span>${dateStr} ${timeStr}</span><span>№: ${data.orderToken}</span></div>
        <div class="receipt-info inline">
          <span>ТТД: ${data.taxId}</span>
          <span>Ажилтан: ${data.employeeName}</span>
        </div>
      </div>
      
      <div class="items">
        <div class="items-header">
          <div class="h-name">Бараа</div>
          <div class="h-price">Үнэ</div>
          <div class="h-qty">Тоо</div>
          <div class="h-total">Дүн</div>
        </div>
        ${itemsHTML}
      </div>
      
      <div class="total-section">
        <div class="items-summary">
          <div>Нийт тоо: ${totalCount}</div>
          <div>Нийт дүн: ${totalAmount.toLocaleString()}</div>
        </div>
        <div class="payment-info">Төлбөр: ${getPaymentMethodText(data.paymentMethod)}</div>
        ${data.companyRegister ? `<div class="payment-info">Байгууллагын регистр: ${data.companyRegister}</div>` : ''}
        ${data.phone ? `<div class="payment-info">Утас: ${data.phone}</div>` : ''}
      </div>
      
      <div class="footer">
        <div>Баярлалаа, дахин ирээрэй</div>
        <div>Хаяг: Саруул зах, Сүхбаатар дүүрэг, УБ</div>
        <div>Онлайн захиалга: Chef.mn</div>
      </div>
    </body>
    </html>
  `;
};

export const generateReceiptHTMLA4 = (data: ReceiptData): string => {
  const now = new Date();
  const dateStr = now.toLocaleDateString('mn-MN');
  const timeStr = now.toLocaleTimeString('mn-MN');
  
  let itemsHTML = '';
  let totalAmount = 0;
  let totalCount = 0;
  
  data.items.forEach((item) => {
    const price = item.saleActive ? item.salePrice : item.price;
    const itemTotal = price * item.count;
    totalAmount += itemTotal;
    totalCount += item.count;
    
    itemsHTML += `
      <div class="item-row">
        <div class="h-name">${item.name}</div>
        <div class="h-price">${price.toLocaleString()}</div>
        <div class="h-qty">${item.count}</div>
        <div class="h-total">${itemTotal.toLocaleString()}</div>
      </div>
    `;
  });

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <title>Баримт - A4</title>
      <style>
        @page {
          size: A4;
          margin: 0;
        }
        body {
          font-family: 'Courier New', monospace;
          font-size: 12px;
          line-height: 1.3;
          margin: 0;
          padding: 0;
          width: 100%;
          color: #000;
          background: #fff;
        }
        .page-container {
          display: flex;
          width: 100%;
          height: 100vh;
        }
        .receipt-column {
          flex: 1;
          width: 50%;
          padding: 20px;
          box-sizing: border-box;
          border-right: 1px solid #ccc;
        }
        .receipt-column:last-child {
          border-right: none;
        }
        .receipt {
          width: 100%;
          display: block;
        }
        .header {
          text-align: center;
          border-bottom: 1px dashed #000;
          padding-bottom: 8px;
          margin-bottom: 8px;
        }
        .company-name {
          font-weight: bold;
          font-size: 16px;
          margin-bottom: 4px;
        }
        .receipt-info {
          margin-bottom: 6px;
          font-size: 11px;
        }
        .inline {
          display: flex;
          justify-content: space-between;
          gap: 8px;
        }
        .item-row {
          display: flex;
          margin-bottom: 4px;
          padding-bottom: 3px;
        }
        .item-row .h-name { 
          flex: 0 0 40%; 
          text-align: left; 
          word-wrap: break-word;
          overflow-wrap: break-word;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
          line-height: 1.2;
        }
        .item-row .h-price { 
          flex: 0 0 20%; 
          text-align: right; 
        }
        .item-row .h-qty { 
          flex: 0 0 10%; 
          text-align: right; 
        }
        .item-row .h-total { 
          flex: 0 0 30%; 
          text-align: right; 
        }
        .total-section {
          margin-top: 8px;
          padding-top: 8px;
          text-align: left;
        }
        .total-amount {
          font-weight: bold;
          margin: 6px 0;
          font-size: 14px;
        }
        .payment-info {
          margin: 6px 0 4px 0;
          font-size: 11px;
        }
        .footer {
          text-align: left;
          margin-top: 15px;
          border-top: 1px dashed #000;
          padding-top: 8px;
          font-size: 10px;
        }
        .divider {
          border-top: 1px dashed #000;
          margin: 6px 0;
        }
        .items-header {
          display: flex;
          margin: 6px 0;
          padding-bottom: 3px;
          border-bottom: 1px dotted #000;
          font-weight: bold;
        }
        .items-header .h-name { flex: 0 0 40%; text-align: left; }
        .items-header .h-price { flex: 0 0 20%; text-align: right; }
        .items-header .h-qty { flex: 0 0 10%; text-align: right; }
        .items-header .h-total { flex: 0 0 30%; text-align: right; }
        .items-summary {
          display: flex;
          justify-content: space-between;
          font-weight: bold;
          margin-top: 8px;
          padding-top: 6px;
          border-top: 1px dashed #000;
          font-size: 13px;
        }
        .items {
          margin-bottom: 10px;
        }
      </style>
    </head>
    <body>
      <div class="page-container">
        <div class="receipt-column">
          <div class="receipt">
            <div class="header">
              <div class="company-name">${data.businessName}</div>
              <div class="divider"></div>
              <div class="receipt-info inline"><span>${dateStr} ${timeStr}</span><span>№: ${data.orderToken}</span></div>
              <div class="receipt-info inline">
                <span>ТТД: ${data.taxId}</span>
                <span>Ажилтан: ${data.employeeName}</span>
              </div>
            </div>
            
            <div class="items">
              <div class="items-header">
                <div class="h-name">Бараа</div>
                <div class="h-price">Үнэ</div>
                <div class="h-qty">Тоо</div>
                <div class="h-total">Дүн</div>
              </div>
              ${itemsHTML}
            </div>
            
            <div class="total-section">
              <div class="items-summary">
                <div>Нийт тоо: ${totalCount}</div>
                <div>Нийт дүн: ${totalAmount.toLocaleString()}</div>
              </div>
              <div class="payment-info">Төлбөр: ${getPaymentMethodText(data.paymentMethod)}</div>
              ${data.companyRegister ? `<div class="payment-info">Байгууллагын регистр: ${data.companyRegister}</div>` : ''}
              ${data.phone ? `<div class="payment-info">Утас: ${data.phone}</div>` : ''}
            </div>
            
            <div class="footer">
              <div>Баярлалаа, дахин ирээрэй</div>
              <div>Хаяг: Саруул зах, Сүхбаатар дүүрэг, УБ</div>
              <div>Онлайн захиалга: Chef.mn</div>
            </div>
          </div>
        </div>
        
        <div class="receipt-column">
          <div class="receipt">
            <div class="header">
              <div class="company-name">${data.businessName}</div>
              <div class="divider"></div>
              <div class="receipt-info inline"><span>${dateStr} ${timeStr}</span><span>№: ${data.orderToken}</span></div>
              <div class="receipt-info inline">
                <span>ТТД: ${data.taxId}</span>
                <span>Ажилтан: ${data.employeeName}</span>
              </div>
            </div>
            
            <div class="items">
              <div class="items-header">
                <div class="h-name">Бараа</div>
                <div class="h-price">Үнэ</div>
                <div class="h-qty">Тоо</div>
                <div class="h-total">Дүн</div>
              </div>
              ${itemsHTML}
            </div>
            
            <div class="total-section">
              <div class="items-summary">
                <div>Нийт тоо: ${totalCount}</div>
                <div>Нийт дүн: ${totalAmount.toLocaleString()}</div>
              </div>
              <div class="payment-info">Төлбөр: ${getPaymentMethodText(data.paymentMethod)}</div>
              ${data.companyRegister ? `<div class="payment-info">Байгууллагын регистр: ${data.companyRegister}</div>` : ''}
              ${data.phone ? `<div class="payment-info">Утас: ${data.phone}</div>` : ''}
            </div>
            
            <div class="footer">
              <div>Баярлалаа, дахин ирээрэй</div>
              <div>Хаяг: Саруул зах, Сүхбаатар дүүрэг, УБ</div>
              <div>Онлайн захиалга: Chef.mn</div>
            </div>
          </div>
        </div>
      </div>
    </body>
    </html>
  `;
};
